import { FileText, FolderOpen } from 'lucide-react';
import { formatDate, formatSquareMeters, type InvestmentResult, type MediaItemRecord } from '@brimo/shared';
import { clsx } from '../../../../ds/clsx';
import { Icon, Pill } from '../../../../ds/components';
import { usePropertyRecord, useUnit } from '../../api';
import { PICTURE_SIZES, pictureOf, sortGallery } from '../../media/gallery';
import type { CalculatorContext, InvestmentInputValues } from '../../types';
import { eur, federalStateOf, pct } from '../model';
import { Badge, Figure, Section } from './parts';
import styles from '../advisor.module.css';

const MAX_PHOTOS = 4;

/** Photos of the unit, else of the property; empty when neither has any (no placeholder, brief §1). */
function photosOf(unitMedia: MediaItemRecord[], propertyMedia: MediaItemRecord[]): MediaItemRecord[] {
  const photos = (items: MediaItemRecord[]) => sortGallery(items).filter((item) => item.kind === 'photo');
  const own = photos(unitMedia);
  return (own.length > 0 ? own : photos(propertyMedia)).slice(0, MAX_PHOTOS);
}

function Photo({ item, main, alt }: { item: MediaItemRecord; main: boolean; alt: string }) {
  const picture = pictureOf(item.fileId, { size: item });
  return (
    <img
      className={main ? styles.photoMain : styles.photoThumb}
      src={picture.src}
      srcSet={picture.srcSet}
      sizes={main ? PICTURE_SIZES.unitSlot : PICTURE_SIZES.galleryTile}
      alt={item.caption ?? alt}
      loading="lazy"
    />
  );
}

export interface UnitSectionProps {
  context: CalculatorContext;
  values: InvestmentInputValues;
  result: InvestmentResult;
}

/** Sections 1 „{Einheit} · {Objekt}" and 2 „Unterlagen zur Wohnung". */
export function UnitSection({ context, values, result }: UnitSectionProps) {
  const { unit } = context;
  const unitQuery = useUnit(unit.id);
  const propertyQuery = usePropertyRecord(unit.propertyId);
  const photos = photosOf(unitQuery.data?.media ?? [], propertyQuery.data?.media ?? []);
  const [mainPhoto, ...thumbs] = photos;
  // The advisor shows this view to the customer: only documents released to the customer.
  const documents = (unitQuery.data?.documents.items ?? []).filter(
    (row) => row.canOpen && row.fileId !== null && row.visibleToCustomer,
  );
  const folderUrl = propertyQuery.data?.fields.documentsUrl ?? null;

  const area = values.areaSqm;
  const pricePerSqm = area > 0 ? values.purchasePriceCents / area : null;
  const state = federalStateOf(context);
  const costsBadge = [state, pct(values.acquisitionCostsPct, 2)].filter(Boolean).join(' · ');
  const loan = result.loanCents;
  const title = `${unit.number} · ${unit.propertyName}`;

  return (
    <>
      <Section id="advisor-unit" title={title}>
        <div className={styles.unitGrid}>
          <div className={styles.identity}>
            {mainPhoto ? (
              <>
                <Photo item={mainPhoto} main alt={title} />
                {thumbs.length > 0 ? (
                  <div className={styles.photoRow}>
                    {thumbs.map((item) => (
                      <Photo key={item.id} item={item} main={false} alt={title} />
                    ))}
                  </div>
                ) : null}
              </>
            ) : null}
            {unit.rented ? (
              <div className={styles.statusRow}>
                <Pill variant="success">
                  {unit.rentedSinceOn ? `Vermietet seit ${formatDate(unit.rentedSinceOn)}` : 'Vermietet'}
                </Pill>
              </div>
            ) : null}
            <div className={styles.identityLines}>
              {unit.address ? <span>{unit.address}</span> : null}
              {unit.floor ? <span className={styles.identityMuted}>Etage: {unit.floor}</span> : null}
              {unit.yearBuilt ? (
                <span className={styles.identityMuted}>Baujahr: {unit.yearBuilt}</span>
              ) : null}
            </div>
          </div>
          <div className={styles.tiles}>
            <div className={clsx(styles.card, styles.tile)}>
              <div className={styles.tileTop}>
                <span className={styles.label}>Kaufpreis</span>
                <div className={styles.valueRow}>
                  <span className={styles.value}>{eur(values.purchasePriceCents)}</span>
                  {pricePerSqm === null ? null : <Badge tone="dark">{eur(pricePerSqm)}/m²</Badge>}
                </div>
              </div>
              <div className={clsx(styles.tileBottom, styles.tileBottomSingle)}>
                <Figure label="Wohnfläche" value={area > 0 ? formatSquareMeters(area) : '–'} small />
              </div>
            </div>
            <div className={clsx(styles.card, styles.tile)}>
              <div className={styles.tileTop}>
                <span className={styles.label}>Kaufnebenkosten</span>
                <div className={styles.valueRow}>
                  <span className={styles.value}>{eur(result.acquisitionCostsCents)}</span>
                  <Badge tone="blue">{costsBadge}</Badge>
                </div>
              </div>
              <div className={clsx(styles.tileBottom, styles.tileBottomSingle)}>
                <Figure label="Ihr Eigenkapital" value={eur(result.equityCents)} small />
              </div>
            </div>
            <div className={clsx(styles.card, styles.tile)}>
              <div className={styles.tileTop}>
                <span className={styles.label}>Laufende Kosten im Monat</span>
                <span className={styles.value}>
                  {eur(values.deductibleCostsCents + values.nonDeductibleCostsCents)}
                </span>
              </div>
              <div className={styles.tileBottom}>
                <Figure label="steuerlich absetzbar" value={eur(values.deductibleCostsCents)} small />
                <Figure label="Rücklage, nicht absetzbar" value={eur(values.nonDeductibleCostsCents)} small />
              </div>
            </div>
            <div className={clsx(styles.card, styles.tile)}>
              <div className={styles.tileTop}>
                <span className={styles.label}>Darlehen</span>
                <span className={styles.value}>{eur(loan)}</span>
              </div>
              <div className={styles.tileBottom}>
                <Figure
                  label={`Tilgung · ${eur((loan * values.repaymentPct) / 100 / 12)} im Monat`}
                  value={pct(values.repaymentPct, 2)}
                  small
                />
                <Figure
                  label={`Zins · ${eur((loan * values.interestPct) / 100 / 12)} im Monat`}
                  value={pct(values.interestPct, 2)}
                  small
                />
              </div>
            </div>
          </div>
        </div>
      </Section>

      {documents.length > 0 || folderUrl ? (
        <Section id="advisor-documents" title="Unterlagen zur Wohnung">
          <div className={styles.docList}>
            {documents.map((row) => (
              <a
                key={row.id}
                className={clsx('pressable', styles.docLink)}
                href={`/api/files/${row.fileId ?? ''}`}
                target="_blank"
                rel="noopener"
              >
                <Icon icon={FileText} size={16} />
                <span className={styles.docName} title={row.name}>
                  {row.name}
                </span>
              </a>
            ))}
            {folderUrl ? (
              <a
                className={clsx('pressable', styles.docLink)}
                href={folderUrl}
                target="_blank"
                rel="noopener"
              >
                <Icon icon={FolderOpen} size={16} />
                <span className={styles.docName}>Unterlagenordner öffnen</span>
              </a>
            ) : null}
          </div>
        </Section>
      ) : null}
    </>
  );
}
